import { useEffect, useRef, useMemo } from 'react';
import { useCurrentUser } from '../api/endpoints/useCurrentUser';
import { useClientId } from './useClientId';
import { useI18n } from '@locus-taxy/encore-i18n-toolkit';
import { DateTimeUtils } from '@locus-taxy/encore-utils';
import { getApiBaseUrl } from '../config';

const useConfigureLocale = () => {
  const clientId = useClientId();

  // Get current user data
  const { data: currentUserResponse } = useCurrentUser(clientId);

  const apiBaseUrl = useMemo(() => {
    if (!clientId) return '';
    return getApiBaseUrl(clientId);
  }, [clientId]);

  const { init18nWithUserLanguage } = useI18n({
    clientId,
    apiBaseUrl,
  });

  const lastAppliedRef = useRef<{
    locale: string | null;
    initFn: typeof init18nWithUserLanguage | null;
  }>({ locale: null, initFn: null });

  const localeIdentifier =
    currentUserResponse?.data?.entityData?.preferences?.localeIdentifier ??
    null;

  const timezone =
    currentUserResponse?.data?.entityData?.preferences?.timezone ?? null;

  useEffect(() => {
    if (!localeIdentifier) return;

    const initFnChanged =
      lastAppliedRef.current?.initFn !== init18nWithUserLanguage;
    const localeChanged = lastAppliedRef.current?.locale !== localeIdentifier;

    if (!initFnChanged && !localeChanged) return;

    init18nWithUserLanguage(localeIdentifier);

    lastAppliedRef.current = {
      locale: localeIdentifier,
      initFn: init18nWithUserLanguage,
    };
  }, [init18nWithUserLanguage, localeIdentifier]);

  useEffect(() => {
    if (!timezone || !localeIdentifier) return;

    const dateTimeUtilsInstance = DateTimeUtils.getInstance();
    dateTimeUtilsInstance.setTimezone(timezone);
    dateTimeUtilsInstance.setLocale(localeIdentifier);
  }, [timezone, localeIdentifier]);
};

export default useConfigureLocale;
